var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useIsMobile } from '@repo/mui-config/hooks';
import { colorPalette } from '@repo/mui-config/color-palette';
import { convertNumberToLocaleMoneyFormat } from '@repo/shared/helpers';
import { SPECIAL_OFFER_LOAN_INTEREST } from '@repo/shared/constants';
import { CustomMarks } from './custom-marks';
import { MoneyInput, Slider, MoneyInputMask } from './data-entry';
export var SpecialOfferBadge = function (_a) {
    var isSelectedSpecialOfferPromotionVariant5 = _a.isSelectedSpecialOfferPromotionVariant5, inline = _a.inline;
    var t = useTranslation().t;
    if (isSelectedSpecialOfferPromotionVariant5) {
        return (_jsx(Box, { sx: __assign(__assign({}, (inline
                ? {}
                : {
                    position: 'absolute',
                    top: 0,
                    right: '50%',
                    transform: 'translate(50%, -50%)',
                })), { backgroundColor: colorPalette.scarlet.main, borderRadius: '4px', padding: '6px 10px', width: 'fit-content' }), children: _jsxs(Typography, { color: colorPalette.white, fontSize: { xs: 10, sm: 12, lg: 14 }, fontWeight: "bold", lineHeight: "18px", children: [t('loanFormPage.advanceAmount.starter'), " ", SPECIAL_OFFER_LOAN_INTEREST, "%"] }) }));
    }
    return (_jsx(Box, { sx: __assign(__assign({}, (inline
            ? {}
            : {
                position: 'absolute',
                top: '50%',
                right: '42%',
                transform: 'translate(50%, -50%)',
            })), { backgroundColor: colorPalette.purple.main, borderRadius: '13px', padding: '4px 8px', width: 'fit-content' }), children: _jsxs(Typography, { color: colorPalette.white, fontSize: { xs: 10, sm: 12, lg: 14 }, lineHeight: "18px", children: [t('loanFormPage.advanceAmount.specialOffer'), " ", SPECIAL_OFFER_LOAN_INTEREST, "%"] }) }));
};
export function AmountSelector(_a) {
    var name = _a.name, max = _a.max, min = _a.min, currency = _a.currency, isSpecialOfferSelected = _a.isSpecialOfferSelected, isSelectedSpecialOfferPromotionVariant5 = _a.isSelectedSpecialOfferPromotionVariant5;
    var t = useTranslation().t;
    var isMobile = useIsMobile();
    var _b = useFormContext(), watch = _b.watch, setValue = _b.setValue;
    var AMOUNT = watch(name);
    var _c = useState(), limitError = _c[0], setLimitError = _c[1];
    useEffect(function () {
        var belowLimit = (limitError === null || limitError === void 0 ? void 0 : limitError.type) === 'BELOW_LIMIT' && AMOUNT > min;
        var overLimit = (limitError === null || limitError === void 0 ? void 0 : limitError.type) === 'OVER_LIMIT' && AMOUNT < max;
        var timeout = setTimeout(function () {
            if (belowLimit || overLimit) {
                setLimitError(undefined);
            }
        }, 1000);
        return function () {
            clearTimeout(timeout);
        };
    }, [AMOUNT, limitError === null || limitError === void 0 ? void 0 : limitError.type, max, min]);
    useEffect(function () {
        var timeout = setTimeout(function () {
            if (AMOUNT < min) {
                setLimitError({
                    type: 'BELOW_LIMIT',
                    message: t('components.amountSelector.minAmountFallShort', {
                        min: convertNumberToLocaleMoneyFormat(min),
                    }),
                });
                setValue('amount', min);
            }
            if (AMOUNT > max) {
                setLimitError({
                    type: 'OVER_LIMIT',
                    message: t('components.amountSelector.maxAmountExceed', {
                        max: convertNumberToLocaleMoneyFormat(max),
                    }),
                });
                setValue('amount', max);
            }
        }, 1000);
        return function () {
            clearTimeout(timeout);
        };
    }, [AMOUNT, max, min, setValue, t]);
    var inputBorder = function () {
        if (isSelectedSpecialOfferPromotionVariant5) {
            return "2px dashed ".concat(colorPalette.scarlet.main, " ");
        }
        return "1px solid ".concat(colorPalette.primary.main, " ");
    };
    return (_jsxs(Box, { sx: { position: 'relative' }, children: [_jsx(MoneyInput, { slotProps: {
                    input: {
                        style: {
                            fontSize: isMobile ? 29 : 56,
                            maxHeight: 100,
                            color: colorPalette.ink.main,
                            fontWeight: 900,
                            borderRadius: 10,
                            backgroundColor: colorPalette.white,
                            paddingLeft: 12,
                            border: inputBorder(),
                        },
                        endAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(Typography, { component: "p", fontWeight: 900, variant: "h2", children: currency }) })),
                        // @ts-expect-error Issue with the component types, more info here: https://github.com/s-yadav/react-number-format/issues/805,
                        // official example with any https://codesandbox.io/p/sandbox/vigorous-zhukovsky-5m7nxt?file=%2Fsrc%2FDemo.tsx%3A89%2C1-90%2C1
                        inputComponent: MoneyInputMask,
                    },
                    htmlInput: {
                        'data-testid': 'money-input',
                    },
                }, name: name }), isSpecialOfferSelected ? (_jsx(SpecialOfferBadge, { isSelectedSpecialOfferPromotionVariant5: isSelectedSpecialOfferPromotionVariant5 })) : null, _jsxs(Box, { children: [(limitError === null || limitError === void 0 ? void 0 : limitError.type) ? (_jsx(Typography, { color: colorPalette.primary.main, component: "div", fontWeight: 500, variant: "textField", children: limitError.message })) : null, _jsx(Slider, { customMarks: _jsx(CustomMarks, { currency: currency, max: max, min: min, isSpecialOfferSelected: isSpecialOfferSelected, isSelectedSpecialOfferPromotionVariant5: isSelectedSpecialOfferPromotionVariant5 }), max: max, min: min, name: name, step: 100, valueLabelFormat: function (value) { return value.toLocaleString(); }, isSpecialOfferSelected: isSpecialOfferSelected, isSelectedSpecialOfferPromotionVariant5: isSelectedSpecialOfferPromotionVariant5 })] })] }));
}
